import FreelancerLayout from "../../layouts/FreelancerLayout";
import { getProfile, pipViewDate2, setProfile } from "../../utils/pip";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { findAJobPaths } from "../../routes/find-a-job";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchfreelancerProfile,
  freelancerUpdateProfileStep,
} from "../../redux/features/find-a-job/actions/authAction";
import TagInput from "../../components/form/TagInput";
import Loader from "../../components/loader";
import ReadMoreText from "../../components/ReadMoreText/ReadMoreText";
import PdfOrImageUploader from "../../components/pdfImage uploader";
import { Formik } from "formik";
import ErrorMessage from "../../components/form/ErrorMessage";
import { message } from "antd";
import {
  experienceModalSchema,
  professionalInfoSchema,
  toolsModalSchema,
  skillsModalSchema,
  workSamplesModalSchema,
  documentsModalSchema,
} from "../../utils/schema";

const Profile = () => {
  const dispatch = useDispatch();
  const { subLoading, freelancerProfileData } = useSelector((state) => state.freelancer.auth);
  const hasServerProfile =
    freelancerProfileData && Object.keys(freelancerProfileData).length > 0;
  const profileData = hasServerProfile
    ? freelancerProfileData
    : getProfile("freelancer");
  const navigate = useNavigate();
  const [isAddExperienceOpen, setIsAddExperienceOpen] = useState(false);
  const [isSubmittingExperience, setIsSubmittingExperience] = useState(false);
  const [isProfessionalEditOpen, setIsProfessionalEditOpen] = useState(false);
  const [isPreferredTypeEditOpen, setIsPreferredTypeEditOpen] = useState(false);
  const [isToolsEditOpen, setIsToolsEditOpen] = useState(false);
  const [isSkillsEditOpen, setIsSkillsEditOpen] = useState(false);
  const [isWorkSamplesEditOpen, setIsWorkSamplesEditOpen] = useState(false);
  const [isDocumentsEditOpen, setIsDocumentsEditOpen] = useState(false);
  const [isSectionSaving, setIsSectionSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchfreelancerProfile()).then((res) => {
      const data = res?.payload?.data;
      if (data) {
        const existing = getProfile("freelancer") || {};
        const merged = {
          ...data,
          full_name:
            data.full_name ||
            existing.full_name ||
            `${data.first_name || ""} ${data.last_name || ""}`.trim(),
          first_name: data.first_name || existing.first_name || data.full_name || "",
          last_name: data.last_name || existing.last_name || "",
        };
        setProfile("freelancer", merged);
      }
    });
  }, [dispatch]);

  const openTalent = profileData?.open_talent || {};
  const fullName =
    profileData?.full_name ||
    `${profileData?.first_name || ""} ${profileData?.last_name || ""}`.trim();
  const locationText =
    openTalent?.location ||
    [openTalent?.city, openTalent?.country].filter(Boolean).join(", ");

  const parseMaybeArray = (value, fallback = []) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return fallback;
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : fallback;
      } catch {
        return [trimmed];
      }
    }
    return fallback;
  };

  const splitList = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (!value) return [];
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const toggleAvailability = (
    currentList,
    value,
    setFieldValue,
    setFieldError,
    setFieldTouched
  ) => {
    const current = new Set(currentList || []);
    if (current.has(value)) {
      current.delete(value);
    } else {
      if (current.size >= 3) {
        setFieldError("availability", undefined);
        message.error("You can select up to 3 availability options.");
        return;
      }
      current.add(value);
    }
    setFieldTouched("availability", true, false);
    setFieldValue("availability", Array.from(current));
  };

  const experiences = parseMaybeArray(
    openTalent?.experiences ?? profileData?.experiences,
    []
  );
  const portfolioLinks = parseMaybeArray(openTalent?.portfolio_links, []);
  const workSamples = parseMaybeArray(openTalent?.work_samples, []);
  const workSampleProjects = Array.isArray(openTalent?.projects)
    ? openTalent.projects
    : [];
  const cvFiles = parseMaybeArray(openTalent?.cv ?? profileData?.cv, []);
  const workSamplesCombined = [
    ...workSampleProjects
      .filter((item) => item?.project_url)
      .map((item) => ({
        url: item.project_url,
        name: item.project_url?.split("/").pop() || "Project",
      })),
    ...workSamples.map((item) => {
      if (typeof item === "string") {
        return {
          url: item,
          name: item.split("/").pop() || item,
        };
      }
      return item;
    }),
  ];

  const certificateFiles = parseMaybeArray(
    openTalent?.certificates ?? profileData?.certificates,
    []
  );
  const keySkillsSource =
    openTalent?.key_skills || openTalent?.skills || "";
  const keySkills =
    typeof keySkillsSource === "string"
      ? keySkillsSource.split(",").map((s) => s.trim()).filter(Boolean)
      : Array.isArray(keySkillsSource)
        ? keySkillsSource
        : [];

  const toolsUsedSource =
    openTalent?.tools || openTalent?.tools_software_used || "";
  const toolsUsed =
    typeof toolsUsedSource === "string"
      ? toolsUsedSource.split(",").map((s) => s.trim()).filter(Boolean)
      : Array.isArray(toolsUsedSource)
        ? toolsUsedSource
        : [];

  const preferredProjectTypes =
    typeof openTalent?.preferred_project_type === "string"
      ? openTalent.preferred_project_type.split(",").map((s) => s.trim()).filter(Boolean)
      : Array.isArray(openTalent?.preferred_project_type)
        ? openTalent.preferred_project_type
        : [];
  const projectTypeOptions = [
    "Short-term projects",
    "Long-term contracts",
    "Startup collaborations",
    "Corporate clients",
  ];
  const roleOptions = [
    "UX Designer",
    "UI Designer",
    "UX Researcher",
    "Graphic Designer",
    "Product Designer",
    "Interaction Designer",
    "Frontend Developer",
    "Backend Developer",
    "Full Stack Developer",
    "Web Developer",
    "Mobile App Developer (iOS)",
    "Mobile App Developer (Android)",
    "Cross-Platform App Developer",
    "Software Engineer",
    "Application Developer",
    "API Developer",
    "DevOps Engineer",
    "Cloud Engineer",
    "Site Reliability Engineer (SRE)",
    "Infrastructure Engineer",
    "Data Analyst",
    "Data Scientist",
    "Machine Learning Engineer",
    "AI Engineer",
    "Data Engineer",
    "Cybersecurity Specialist",
    "Security Engineer",
    "Penetration Tester",
    "QA Engineer",
    "Test Automation Engineer",
    "Manual Tester",
    "Database Administrator",
    "Systems Administrator",
    "Network Engineer",
    "IT Support Specialist",
    "Blockchain Developer",
    "Game Developer",
    "Embedded Systems Engineer",
    "Technical Project Manager",
    "IT Project Manager",
    "Product Manager",
    "Scrum Master",
    "Agile Coach",
    "Business Analyst",
    "Technical Consultant",
    "Solutions Architect",
    "Enterprise Architect",
    "Other",
  ];
  const availabilityOptions = [
    { label: "Remote Freelance", value: "Remote Freelance" },
    { label: "Full-time Freelance", value: "Full-time Freelance" },
    { label: "Part-time Freelance", value: "Part-time Freelance" },
    { label: "Hybrid Freelance", value: "Hybrid Freelance" },
    { label: "On-Site Freelance", value: "On-Site Freelance" },
    { label: "Project-Based Freelance", value: "Project Based" },
  ];
  const linkRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;
  const isValidLink = (value) => linkRegex.test(value || "");
  const toCommaString = (list = []) =>
    Array.isArray(list)
      ? list.map((s) => String(s || "").trim()).filter(Boolean).join(", ")
      : "";
  const normalizeList = (value) =>
    Array.isArray(value)
      ? value.map((s) => String(s || "").trim()).filter(Boolean)
      : String(value || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
  const normalizeAvailabilityList = (value) => {
    const availabilityMap = {
      remote_freelance: "Remote Freelance",
      full_time_freelance: "Full-time Freelance",
      part_time_freelance: "Part-time Freelance",
      hybrid_freelance: "Hybrid Freelance",
      on_site_freelance: "On-Site Freelance",
      onsite_freelance: "On-Site Freelance",
      project_based: "Project Based",
      project_based_freelance: "Project Based",
    };
    return normalizeList(value).map((item) => {
      const key = String(item || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");
      if (availabilityMap[key]) return availabilityMap[key];
      if (availabilityMap[item]) return availabilityMap[item];
      const titleCased = key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (match) => match.toUpperCase());
      return titleCased
        .replace("On Site", "On-Site")
        .replace("Part Time", "Part-time")
        .replace("Full Time", "Full-time");
    });
  };
  const companyDetails =
    openTalent?.company_details ||
    openTalent?.company_description ||
    openTalent?.company_bio ||
    "";

  const [projectSkills, setProjectSkills] = useState([]);

  if (subLoading) {
    return <Loader />
  }
  return (
    <FreelancerLayout>
      <div className="ct_px_40 my-5">
        <div className="container-fluid">
          <div className="row">
            <div className="col-xl-6 mb-4 mb-xl-0">
              <div>
                <div className="ct_outline_bg h-100 ct_border_radius_10 ct_p_20">
                  <div className="d-flex align-items-start gap-3 ct_flex_col_575 ">
                    <div className="ct_profile_pic ct_flex_shrink_0">
                      <img
                        src={
                          profileData?.profile_image ||
                          openTalent?.profile_image ||
                          "/assets/img/user_profile.png"
                        }
                      />
                    </div>
                    <div className="ct_flex_1">
                      <div className="d-flex align-items-center gap-3 justify-content-between mb-3 ">
                        <div>
                          <h4 className="ct_fs_20 ct_darkblue_text mb-2">
                            {fullName || "#N/A"}
                          </h4>
                          <p className="ct_fs_14 ct_darkblue_text mb-0">
                            <i class="fa-solid fa-location-dot"></i>{" "}
                            {locationText || "#N/A"}
                          </p>
                        </div>
                        <div>
                          <button
                            className="ct_yellow_btn ct_sky_blue_btn ct_h_32 ct_white_nowrap"
                            type="button"
                            onClick={() => navigate(findAJobPaths.EditProfile)}
                          >
                            Edit Profile
                          </button>
                        </div>
                      </div>
                      <div className="ct_grey_text">
                        <ReadMoreText text={profileData?.open_talent?.bio || ""} />
                      </div>
                      {companyDetails && (
                        <div className="ct_grey_text ct_mt_16">
                          <p className="ct_fs_14 ct_darkblue_text mb-1">
                            Company Details
                          </p>
                          <ReadMoreText text={companyDetails} />
                        </div>
                      )}
                      <ul>
                        <li className="d-flex align-items-center gap-2">
                          <img src="../assets/img/grey_mail_icon.svg" />
                          {profileData?.email || ""}
                        </li>
                        <li className="d-flex align-items-center gap-2">
                          <img src="../assets/img/phone_789.svg" />
                          {profileData?.phone_number || ""}
                        </li>
                      </ul>
                    </div>
                  </div>
                  <ul className="d-flex align-items-center gap-4 flex-wrap ct_mt_40">
                    <li>
                      <div className="d-flex align-items-center gap-2">
                        <img src="../assets/img/tabler_clock.svg" />
                        <div>
                          <h4 className="ct_fs_16 mb-1 ct_darkblue_text">
                            {openTalent?.years_of_experience || "#N/A"} Experience
                          </h4>
                          <p className="ct_fs_14 ct_grey_text mb-0">Experience</p>
                        </div>
                      </div>
                    </li>
                    {profileData?.open_talent?.certificates?.length > 0 && (
                      <li>
                        <div className="d-flex align-items-center gap-2">
                          <img src="../assets/img/hugeicons_award-01.svg" />
                          <div>
                            <h4 className="ct_fs_16 mb-1 ct_darkblue_text">
                              {profileData?.open_talent?.certificates?.length || ""} Certificate
                              {profileData?.open_talent?.certificates?.length > 1 ? "s" : ""}
                            </h4>
                            <p className="ct_fs_14 ct_grey_text mb-0">Advanced</p>
                          </div>
                        </div>
                      </li>
                    )}
                    <li>
                      <div className="d-flex align-items-center gap-2">
                        <img src="../assets/img/hugeicons_job-link.svg" />
                        <div>
                          <h4 className="ct_fs_16 mb-1 ct_darkblue_text">
                            {experiences.length} Experience
                            {experiences.length === 1 ? "" : "s"}
                          </h4>
                          <p className="ct_fs_14 ct_grey_text mb-0">Completed</p>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="ct_outline_bg h-auto ct_border_radius_10 ct_p_20 ct_mt_20">
                <div className="">
                  <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                    <p className="ct_fs_16 mb-0 ct_darkblue_text">Professional Information</p>
                    <button
                      type="button"
                      className="ct_orange_link_underline ct_fs_14"
                      style={{ background: "transparent", border: "0", padding: "0" }}
                      onClick={() => setIsProfessionalEditOpen(true)}
                      aria-label="Edit Professional Information"
                    >
                      <i className="fa-solid fa-pen-to-square"></i>
                    </button>
                  </div>
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <p className="ct_fs_14 ct_grey_text mb-0">Primary Role / Profession</p>
                      <p className="ct_fs_14 ct_darkblue_text mb-0">{openTalent?.primary_role || "#N/A"}</p>
                    </div>
                    <div className="ct_mt_10">
                      <p className="ct_fs_14 ct_grey_text mb-2">Availability</p>
                      {normalizeAvailabilityList(openTalent?.availability).length ? (
                        <ul className="d-flex align-items-center gap-3 flex-wrap cr_para_scroll ct_custom_scroll mb-0">
                          {normalizeAvailabilityList(openTalent?.availability).map((item, index) => (
                            <li className="mb-2" key={`availability-${index}`}>
                              <span className="ct_shadow_badge">{item}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="ct_fs_14 ct_darkblue_text mb-0">#N/A</p>
                      )}
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <p className="ct_fs_14 ct_grey_text mb-0">LinkedIn Profile</p>
                      <a className="ct_fs_14 ct_darkblue_text mb-0" href={openTalent?.linkedin_profile} target="_blank">
                        {openTalent?.linkedin_profile || "#N/A"}
                      </a>
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <p className="ct_fs_14 ct_grey_text mb-0">Portfolio</p>
                      <a className="ct_fs_14 ct_darkblue_text mb-0" href={openTalent?.website_or_portfolio} target="_blank">
                        {openTalent?.website_or_portfolio || "#N/A"}
                      </a>
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <p className="ct_fs_14 ct_grey_text mb-0">Hourly Rate (Optional)</p>
                      <p className="ct_fs_14 ct_darkblue_text mb-0">{openTalent?.hourly_rate || "#N/A"}</p>
                    </div>
                  </div>
                </div>
                {preferredProjectTypes?.length > 0 && (
                  <div className="ct_mt_28">
                    <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                      <p className="ct_fs_16 mb-0 ct_darkblue_text">Preferred Project Type</p>
                      <button
                        type="button"
                        className="ct_orange_link_underline ct_fs_14"
                        style={{ background: "transparent", border: "0", padding: "0" }}
                        onClick={() => setIsPreferredTypeEditOpen(true)}
                        aria-label="Edit Preferred Project Type"
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                      </button>
                    </div>
                    <ul className="d-flex align-items-center gap-3 flex-wrap cr_para_scroll ct_custom_scroll">
                      {preferredProjectTypes.map((item, index) => (
                        <li className="mb-2" key={`project-type-${index}`}>
                          <span className="ct_shadow_badge">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {toolsUsed?.length > 0 && (
                  <div className="ct_mt_28">
                    <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                      <p className="ct_fs_16 mb-0 ct_darkblue_text">Tools & Software Used</p>
                      <button
                        type="button"
                        className="ct_orange_link_underline ct_fs_14"
                        style={{ background: "transparent", border: "0", padding: "0" }}
                        onClick={() => setIsToolsEditOpen(true)}
                        aria-label="Edit Tools & Software"
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                      </button>
                    </div>
                    <ul className="d-flex align-items-center gap-3 flex-wrap cr_para_scroll ct_custom_scroll">
                      {toolsUsed.map((item, index) => (
                        <li className="mb-2" key={`tool-${index}`}>
                          <span className="ct_shadow_badge">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {profileData?.open_talent?.skills?.split(",")?.length > 0 && (
                  <div className="ct_mt_28">
                    <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                      <p className="ct_fs_16 mb-0 ct_darkblue_text">Skills</p>
                      <button
                        type="button"
                        className="ct_orange_link_underline ct_fs_14"
                        style={{ background: "transparent", border: "0", padding: "0" }}
                        onClick={() => setIsSkillsEditOpen(true)}
                        aria-label="Edit Skills"
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                      </button>
                    </div>
                    <ul className="d-flex align-items-center gap-3 flex-wrap cr_para_scroll ct_custom_scroll">
                      {profileData?.open_talent?.skills
                        ?.split(",")
                        ?.map((item, index) => (
                          <li className="mb-2">
                            <span className="ct_shadow_badge">{item}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div className="col-xl-6 mb-4 mb-xl-0">
              <div>
                <div className="ct_outline_bg h-100 ct_border_radius_10 ct_p_20">
                  <div className="d-flex align-items-center justify-content-between gap-3">
                    <h5 className="mb-0 ct_fs_20 ct_darkblue_text">
                      Job Experience
                    </h5>
                    <button
                      type="button"
                      className="ct_orange_link_underline ct_fs_14"
                      style={{ background: "transparent", border: "0", padding: "0" }}
                      onClick={() => setIsAddExperienceOpen(true)}
                      aria-label="Add More Experience"
                    >
                      Add More +
                    </button>
                  </div>
                  <ul className="ct_mt_30">
                    {experiences?.length > 0 ? (
                      experiences.map((exp, idx) => (
                        <li key={`experience-${idx}`}>
                          {(() => {
                            const rawImage =
                              exp?.image ||
                              exp?.experience_images ||
                              exp?.experience_image ||
                              "";
                            const imageUrl = Array.isArray(rawImage)
                              ? rawImage[0]
                              : rawImage;
                            return (
                              <div className="d-flex align-items-start gap-3 ct_flex_col_575 mb-2">
                                <div className="ct_flex_shrink_0">
                                  <img
                                    src={imageUrl || "/assets/img/company.png"}
                                    className="ct_img_40"
                                    style={{ borderRadius: "50%", objectFit: "cover" }}
                                  />
                                </div>
                                <div className="ct_flex_1">
                                  <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                                    <div>
                                      <h5 className="ct_fs_16 mb-2">
                                        {exp?.job_title || "Job Title"}
                                      </h5>
                                      <p className="mb-0 ct_fs_16 ct_grey_text">
                                        {exp?.company_name || "Company Name"}
                                      </p>
                                    </div>
                                    <div className="d-flex align-items-center gap-4 flex-wrap">
                                      <p className="d-flex align-items-center gap-2 ct_fs_12 mb-0">
                                        <svg
                                          width="18"
                                          height="18"
                                          viewBox="0 0 18 18"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M2.25 9C2.25 9.88642 2.42459 10.7642 2.76381 11.5831C3.10303 12.4021 3.60023 13.1462 4.22703 13.773C4.85382 14.3998 5.59794 14.897 6.41689 15.2362C7.23583 15.5754 8.11358 15.75 9 15.75C9.88642 15.75 10.7642 15.5754 11.5831 15.2362C12.4021 14.897 13.1462 14.3998 13.773 13.773C14.3998 13.1462 14.897 12.4021 15.2362 11.5831C15.5754 10.7642 15.75 9.88642 15.75 9C15.75 7.20979 15.0388 5.4929 13.773 4.22703C12.5071 2.96116 10.7902 2.25 9 2.25C7.20979 2.25 5.4929 2.96116 4.22703 4.22703C2.96116 5.4929 2.25 7.20979 2.25 9Z"
                                            stroke="#535862"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M9 5.25V9L10.5 11.5"
                                            stroke="#535862"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                        {exp?.start_date
                                          ? `${pipViewDate2(exp.start_date)} - ${exp?.currently_working
                                            ? "Present"
                                            : exp?.end_date
                                              ? pipViewDate2(exp.end_date)
                                              : "#N/A"
                                          }`
                                          : "#N/A"}
                                      </p>
                                      <p className="d-flex align-items-start gap-2 ct_fs_12 mb-0">
                                        <svg
                                          className="ct_flex_shrink_0"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 16 16"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                          style={{ marginTop: "2px" }}
                                        >
                                          <path
                                            d="M8 1.3335C5.42267 1.3335 3.33333 3.42283 3.33333 6.00016C3.33333 9.50016 8 14.6668 8 14.6668C8 14.6668 12.6667 9.50016 12.6667 6.00016C12.6667 3.42283 10.5773 1.3335 8 1.3335ZM8 8.00016C6.89533 8.00016 6 7.10483 6 6.00016C6 4.8955 6.89533 4.00016 8 4.00016C9.10467 4.00016 10 4.8955 10 6.00016C10 7.10483 9.10467 8.00016 8 8.00016Z"
                                            fill="#535862"
                                          />
                                        </svg>
                                        <span style={{ wordBreak: "break-word" }}>
                                          {exp?.location || "#N/A"}
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                  <p
                                    className="ct_para_scroll ct_custom_scroll ct_fs_15 mt-3 ct_darkblue_text"
                                    style={{ maxHeight: "150px" }}
                                  >
                                    {exp?.job_description || "No description added yet."}
                                  </p>
                                </div>
                              </div>
                            );
                          })()}
                        </li>
                      ))
                    ) : (
                      <li>
                        <p className="ct_grey_text mb-0">No experiences added yet.</p>
                      </li>
                    )}
                  </ul>
                </div>
                <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_20">
                  <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                    <p className="ct_fs_16 mb-0 ct_darkblue_text">Work Samples</p>
                    <button
                      type="button"
                      className="ct_orange_link_underline ct_fs_14"
                      style={{ background: "transparent", border: "0", padding: "0" }}
                      onClick={() => setIsWorkSamplesEditOpen(true)}
                      aria-label="Edit Work Samples"
                    >
                      <i className="fa-solid fa-pen-to-square"></i>
                    </button>
                  </div>
                  {openTalent?.sample_portfolio_link
                    ?.split(",")
                    ?.map((link) => link.trim())
                    ?.filter(Boolean)?.length > 0 && (
                      <>
                        <p className="ct_fs_14 ct_darkblue_text mb-2">
                          Work Sample Links
                        </p>
                        <div className="ct_work_sample_list ct_mb_20">
                          {openTalent?.sample_portfolio_link
                            ?.split(",")
                            ?.map((link) => link.trim())
                            ?.filter(Boolean)
                            ?.map((link, index) => (
                              <div
                                key={`work-sample-link-${index}`}
                                className="ct_work_sample_item"
                              >
                                <span className="ct_badge_grey ct_work_sample_label">
                                  {link}
                                </span>
                                <a
                                  className="ct_outline_btn ct_h_32 ct_btn_inline"
                                  href={link}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View
                                </a>
                              </div>
                            ))}
                        </div>
                      </>
                    )}
                  <p className="ct_fs_14 ct_darkblue_text mb-2">Work Sample Files</p>
                  {workSamplesCombined.length > 0 ? (
                    <div className="ct_work_sample_list">
                      {workSamplesCombined.map((item, index) => {
                        const label =
                          item?.name ||
                          item?.project_url?.split("/").pop() ||
                          item?.url?.split("/").pop() ||
                          `Sample ${index + 1}`;
                        const link = item?.url || item?.project_url;
                        return (
                          <div
                            key={`${label}-${index}`}
                            className="ct_work_sample_item"
                          >
                            <span className="ct_badge_grey ct_work_sample_label">
                              {label}
                            </span>
                            {link && (
                              <a
                                className="ct_outline_btn ct_h_32 ct_btn_inline"
                                href={link}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="ct_grey_text mb-0">No work samples added yet.</p>
                  )}
                </div>
                {(cvFiles.length > 0 || certificateFiles.length > 0) && (
                  <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_20">
                    <div className="d-flex align-items-center justify-content-between gap-3 ct_mb_20">
                      <p className="ct_fs_16 mb-0 ct_darkblue_text">Documents</p>
                      <button
                        type="button"
                        className="ct_orange_link_underline ct_fs_14"
                        style={{ background: "transparent", border: "0", padding: "0" }}
                        onClick={() => setIsDocumentsEditOpen(true)}
                        aria-label="Edit Documents"
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                      </button>
                    </div>
                    {cvFiles.length > 0 && (
                      <div className="mb-3">
                        <p className="ct_fs_14 ct_darkblue_text mb-1">CV</p>
                        <ul className="ct_para_scroll ct_custom_scroll ct_documents_lists mt-2">
                          {cvFiles.map((file, idx) => {
                            const link =
                              typeof file === "string"
                                ? file
                                : file?.url || file?.preview || "";
                            const label =
                              typeof file === "string"
                                ? file.split("/").pop()
                                : file?.name || `cv-${idx + 1}.pdf`;
                            return (
                              <li key={`cv-${idx}`}>
                                <p className="mb-0 d-flex align-items-center gap-2">
                                  <i class="fa-regular fa-file-pdf"></i>
                                  <span className="text-truncate ct_fs_14">
                                    {label}
                                  </span>
                                </p>
                                <p className="mb-0 ct_down_documents_icon">
                                  <a href={link} target="_blank">
                                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                                  </a>
                                </p>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    {certificateFiles.length > 0 && (
                      <div>
                        <p className="ct_fs_14 ct_darkblue_text mb-1">
                          Certificates
                        </p>
                        <ul className="ct_para_scroll ct_custom_scroll ct_documents_lists mt-2">
                          {certificateFiles.map((file, idx) => {
                            const link =
                              typeof file === "string"
                                ? file
                                : file?.file || file?.url || file?.preview || "";
                            const label =
                              typeof file === "string"
                                ? file.split("/").pop()
                                : file?.name ||
                                file?.file?.split?.("/").pop?.() ||
                                `certificate-${idx + 1}`;
                            const isPdf =
                              typeof file === "string"
                                ? file.toLowerCase().includes(".pdf")
                                : (file?.type || "")
                                  .toLowerCase()
                                  .includes("pdf");
                            return (
                              <li key={`certificate-${idx}`}>
                                <p className="mb-0 d-flex align-items-center gap-2">
                                  <i
                                    class={
                                      isPdf
                                        ? "fa-regular fa-file-pdf"
                                        : "fa-regular fa-image"
                                    }
                                  ></i>
                                  <span className="text-truncate ct_fs_14">
                                    {label}
                                  </span>
                                </p>
                                <p className="mb-0 ct_down_documents_icon">
                                  <a href={link} target="_blank">
                                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                                  </a>
                                </p>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                <div className="ct_outline_bg h-100 ct_border_radius_10 ct_p_20 ct_mt_20">
                  <div className="row">
                    <div className="col-md-10 mx-auto">
                      <div className="text-center p-3">
                        <svg
                          width="75"
                          height="75"
                          viewBox="0 0 75 75"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M12.5 2.08333C12.5 1.5308 12.7195 1.0009 13.1102 0.610195C13.5009 0.219494 14.0308 0 14.5833 0H60.4167C60.9692 0 61.4991 0.219494 61.8898 0.610195C62.2805 1.0009 62.5 1.5308 62.5 2.08333V4.16667H72.9167C73.4692 4.16667 73.9991 4.38616 74.3898 4.77686C74.7805 5.16756 75 5.69747 75 6.25V18.75C75 21.5127 73.9025 24.1622 71.949 26.1157C69.9955 28.0692 67.346 29.1667 64.5833 29.1667H61.0771C59.5874 33.3686 57.0029 37.0967 53.5907 39.9659C50.1784 42.835 46.062 44.7412 41.6667 45.4875V58.3333H54.1667C54.7192 58.3333 55.2491 58.5528 55.6398 58.9435C56.0305 59.3342 56.25 59.8641 56.25 60.4167V72.9167C56.25 73.4692 56.0305 73.9991 55.6398 74.3898C55.2491 74.7805 54.7192 75 54.1667 75H20.8333C20.2808 75 19.7509 74.7805 19.3602 74.3898C18.9695 73.9991 18.75 73.4692 18.75 72.9167V60.4167C18.75 59.8641 18.9695 59.3342 19.3602 58.9435C19.7509 58.5528 20.2808 58.3333 20.8333 58.3333H33.3333V45.4875C28.938 44.7412 24.8216 42.835 21.4093 39.9659C17.9971 37.0967 15.4126 33.3686 13.9229 29.1667H10.4167C7.65399 29.1667 5.00447 28.0692 3.05097 26.1157C1.09747 24.1622 0 21.5127 0 18.75V6.25C0 5.69747 0.219494 5.16756 0.610195 4.77686C1.0009 4.38616 1.5308 4.16667 2.08333 4.16667H12.5V2.08333ZM62.5 20.8333V8.33333H70.8333V18.75C70.8333 20.4076 70.1749 21.9973 69.0027 23.1694C67.8306 24.3415 66.2409 25 64.5833 25H62.5V20.8333ZM12.5 8.33333H4.16667V18.75C4.16667 20.4076 4.82515 21.9973 5.99725 23.1694C7.16935 24.3415 8.75906 25 10.4167 25H12.5V8.33333Z"
                            fill="#FFB703"
                          />
                        </svg>
                      </div>
                      <div>
                        <h4 className="ct_fs_28 ct_darkblue_text text-center mt-0 mb-3">
                          Upgrade to Pro{" "}
                        </h4>
                        <p className="ct_darkblue_text text-center">
                          Get more visibility, better opportunities and growth
                          support
                        </p>
                        <ul>
                          <li className="ct_fs_14 ct_darkblue_text d-flex align-items-center gap-2">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 23.25C18.2132 23.25 23.25 18.2132 23.25 12C23.25 5.7868 18.2132 0.75 12 0.75C5.7868 0.75 0.75 5.7868 0.75 12C0.75 18.2132 5.7868 23.25 12 23.25Z"
                                fill="#8ECAE6"
                              />
                              <path
                                d="M17.25 5.25L9.375 13.35L6.75 10.65L4.125 13.35L9.375 18.75L19.875 7.95L17.25 5.25Z"
                                fill="white"
                              />
                            </svg>
                            NuGlow - Kickstart your journey with curated jobs
                            and profile visibility
                          </li>
                          <li className="ct_fs_14 ct_darkblue_text mt-2 d-flex align-items-center gap-2">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 23.25C18.2132 23.25 23.25 18.2132 23.25 12C23.25 5.7868 18.2132 0.75 12 0.75C5.7868 0.75 0.75 5.7868 0.75 12C0.75 18.2132 5.7868 23.25 12 23.25Z"
                                fill="#8ECAE6"
                              />
                              <path
                                d="M17.25 5.25L9.375 13.35L6.75 10.65L4.125 13.35L9.375 18.75L19.875 7.95L17.25 5.25Z"
                                fill="white"
                              />
                            </svg>
                            NuForge - Grow faster with access to premium
                            listings and tools
                          </li>
                          <li className="ct_fs_14 ct_darkblue_text mt-2 d-flex align-items-center gap-2">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 23.25C18.2132 23.25 23.25 18.2132 23.25 12C23.25 5.7868 18.2132 0.75 12 0.75C5.7868 0.75 0.75 5.7868 0.75 12C0.75 18.2132 5.7868 23.25 12 23.25Z"
                                fill="#8ECAE6"
                              />
                              <path
                                d="M17.25 5.25L9.375 13.35L6.75 10.65L4.125 13.35L9.375 18.75L19.875 7.95L17.25 5.25Z"
                                fill="white"
                              />
                            </svg>
                            NuStar - Stand out with early job invites, brand
                            support and pro-level exposure
                          </li>
                        </ul>
                        <div className="mt-4  text-center">
                          <button className="ct_yellow_btn mx-auto ct_sky_blue_btn ct_px_60">
                            Explore Plans
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >

      {isAddExperienceOpen && (
        <div className="modal modal-lg ct_congratulation_modal_fade d-block">
          <div className="modal-dialog modal-dialog-centered ct_modal-dialog">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h4 className="ct_fs_16 ct_darkblue_text mb-0">
                  Add Experience
                </h4>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => setIsAddExperienceOpen(false)}
                ></button>
              </div>
              <div className="modal-body px-0 ct_mt_20 pt-0">
                <Formik
                  initialValues={{
                    job_title: "",
                    company_name: "",
                    location: "",
                    start_date: "",
                    end_date: "",
                    currently_working: false,
                    job_description: "",
                    experience_images: "",
                  }}
                  validationSchema={experienceModalSchema}
                  onSubmit={async (values) => {
                    setIsSubmittingExperience(true);
                    const endDate =
                      values.currently_working || !values.end_date
                        ? null
                        : values.end_date;
                    const resolveExperienceImage = (value) => {
                      if (!value) return { file: null, value: "" };
                      const item = Array.isArray(value) ? value[0] : value;
                      if (!item) return { file: null, value: "" };
                      if (item instanceof File) return { file: item, value: "" };
                      if (item?.file instanceof File)
                        return { file: item.file, value: "" };
                      if (typeof item === "string") return { file: null, value: item };
                      if (typeof item?.url === "string") return { file: null, value: item.url };
                      if (typeof item?.preview === "string")
                        return { file: null, value: item.preview };
                      if (typeof item?.file === "string")
                        return { file: null, value: item.file };
                      return { file: null, value: "" };
                    };

                    const experienceImageFiles = [];

                    const resolvedImage = resolveExperienceImage(values.experience_images);
                    if (resolvedImage.file) {
                      experienceImageFiles.push(resolvedImage.file);
                    }

                    const newExperience = {
                      job_title: values.job_title,
                      company_name: values.company_name,
                      location: values.location,
                      start_date: values.start_date,
                      end_date: endDate,
                      currently_working: values.currently_working,
                      job_description: values.job_description,
                      experience_images: resolvedImage.value || "",
                    };

                    const formData = new FormData();
                    formData.append(
                      "experiences",
                      JSON.stringify([newExperience])
                    );
                    experienceImageFiles.forEach((file) => {
                      formData.append("experience_images", file);
                    });

                    dispatch(
                      freelancerUpdateProfileStep({
                        payload: formData,
                        callback: (response) => {
                          setIsSubmittingExperience(false);
                          if (!response?.success) return;
                          dispatch(fetchfreelancerProfile());
                          setIsAddExperienceOpen(false);
                        },
                      })
                    );
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                  }) => (
                    <form
                      onSubmit={handleSubmit}
                      className="ct_modal_scroll ct_custom_scroll px-4 pb-3 "
                    >
                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              Job Title
                            </label>
                            <input
                              type="text"
                              className="form-control ct_fs_12 ct_input"
                              placeholder="Job title"
                              name="job_title"
                              value={values.job_title}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="job_title"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              Company Name
                            </label>
                            <input
                              type="text"
                              className="form-control ct_fs_12 ct_input"
                              placeholder="Company name"
                              name="company_name"
                              value={values.company_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="company_name"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              Location
                            </label>
                            <input
                              type="text"
                              className="form-control ct_fs_12 ct_input"
                              placeholder="Location"
                              name="location"
                              value={values.location}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="location"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              Start Date
                            </label>
                            <input
                              type="date"
                              className="form-control ct_fs_12 ct_input"
                              name="start_date"
                              max={new Date().toISOString().split("T")[0]}
                              value={values.start_date}
                              onChange={(event) => {
                                const nextStartDate = event.target.value;
                                setFieldValue("start_date", nextStartDate);
                                if (
                                  values.end_date &&
                                  nextStartDate &&
                                  values.end_date < nextStartDate
                                ) {
                                  setFieldValue("end_date", nextStartDate);
                                }
                              }}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="start_date"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              End Date
                            </label>
                            <input
                              type="date"
                              className="form-control ct_fs_12 ct_input"
                              name="end_date"
                              max={new Date().toISOString().split("T")[0]}
                              min={values.start_date || ""}
                              value={values.end_date}
                              onChange={(event) => {
                                const nextEndDate = event.target.value;
                                if (
                                  values.start_date &&
                                  nextEndDate &&
                                  nextEndDate < values.start_date
                                ) {
                                  setFieldValue("end_date", values.start_date);
                                } else {
                                  setFieldValue("end_date", nextEndDate);
                                }
                              }}
                              onBlur={handleBlur}
                              disabled={values.currently_working}
                            />
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="end_date"
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-3">
                            <label className="ct_fs_14 ct_darkblue_text mb-2 d-flex align-items-center gap-2">
                              <input
                                type="checkbox"
                                name="currently_working"
                                checked={values.currently_working}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setFieldValue("currently_working", checked);
                                  if (checked) {
                                    setFieldValue("end_date", "");
                                  }
                                }}
                              />
                              <span>Currently working here</span>
                            </label>
                          </div>
                        </div>

                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              Work Experience
                            </label>
                            <textarea
                              className="form-control ct_input h-auto ct_fs_12"
                              rows={4}
                              placeholder="Briefly describe your role and responsibilities"
                              name="job_description"
                              value={values.job_description}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            ></textarea>
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="job_description"
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              Upload Company Image
                            </label>
                            <PdfOrImageUploader
                              maxFiles={1}
                              multiple={false}
                              acceptType="image/*"
                              placeholder="Upload Company Image"
                              replaceOnSelect={true}
                              existingFiles={
                                values?.experience_images
                                  ? [
                                    (() => {
                                      const raw = values.experience_images;
                                      const item = Array.isArray(raw) ? raw[0] : raw;
                                      if (!item) return null;
                                      if (item instanceof File) {
                                        return {
                                          id: item.name || "experience-image",
                                          file: item,
                                          name: item.name || "Company Image",
                                          type: item.type || "image",
                                          preview: URL.createObjectURL(item),
                                          isExisting: false,
                                        };
                                      }
                                      if (item?.file instanceof File) {
                                        return {
                                          id: item.id || "experience-image",
                                          file: item.file,
                                          name:
                                            item.name || item.file.name || "Company Image",
                                          type: item.type || item.file.type || "image",
                                          preview:
                                            item.preview || URL.createObjectURL(item.file),
                                          isExisting: false,
                                        };
                                      }
                                      const url =
                                        (typeof item === "string" && item) ||
                                        item?.url ||
                                        item?.preview ||
                                        (typeof item?.file === "string" ? item.file : "");
                                      if (!url) return null;
                                      return {
                                        id: item?.id || "experience-image",
                                        file: url,
                                        name:
                                          item?.name ||
                                          (typeof url === "string"
                                            ? url.split("/").pop()
                                            : "Company Image"),
                                        type: item?.type || "image",
                                        preview: url,
                                        isExisting: true,
                                      };
                                    })(),
                                  ].filter(Boolean)
                                  : []
                              }
                              onChange={(newFiles) => {
                                setFieldValue(
                                  "experience_images",
                                  newFiles?.[0] || ""
                                );
                              }}
                            />
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="experience_images"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3 justify-content-end pt-4">
                        <button
                          className="ct_sky_blue_btn ct_fs_14 ct_yellow_btn ct_outline_grey  px-5 py-2"
                          style={{ borderColorL: "#53586252" }}
                          type="button"
                          onClick={() => setIsAddExperienceOpen(false)}
                          disabled={isSubmittingExperience}
                        >
                          Cancel
                        </button>
                        <button
                          className=" ct_yellow_btn px-5 ct_fs_14 py-2"
                          type="submit"
                          disabled={isSubmittingExperience}
                        >
                          {isSubmittingExperience ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}

      {isProfessionalEditOpen && (
        <div className="modal modal-lg ct_congratulation_modal_fade d-block">
          <div className="modal-dialog modal-dialog-centered ct_modal-dialog">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h4 className="ct_fs_16 ct_darkblue_text mb-0">
                  Professional Information
                </h4>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => setIsProfessionalEditOpen(false)}
                ></button>
              </div>
              <div className="modal-body px-0 ct_mt_20 pt-0">
                <Formik
                  initialValues={{
                    primary_role:
                      openTalent?.primary_role &&
                        !roleOptions.includes(openTalent?.primary_role)
                        ? "Other"
                        : openTalent?.primary_role || "",
                    other_primary_role:
                      openTalent?.primary_role &&
                        !roleOptions.includes(openTalent?.primary_role)
                        ? openTalent?.primary_role
                        : "",
                    availability: normalizeAvailabilityList(openTalent?.availability),
                    linkedin_profile: openTalent?.linkedin_profile || "",
                    website_or_portfolio: openTalent?.website_or_portfolio || "",
                    hourly_rate: openTalent?.hourly_rate || "",
                  }}
                  validationSchema={professionalInfoSchema}
                  onSubmit={(values) => {
                    setIsSectionSaving(true);
                    const changes = {};
                    const resolvedPrimaryRole =
                      values.primary_role === "Other"
                        ? values.other_primary_role || ""
                        : values.primary_role || "";
                    if (resolvedPrimaryRole !== (openTalent?.primary_role || "")) {
                      changes.primary_role = resolvedPrimaryRole;
                    }
                    const availabilityValue = (values.availability || []).join(", ");
                    if (availabilityValue !== (openTalent?.availability || "")) {
                      changes.availability = availabilityValue;
                    }
                    if (values.linkedin_profile !== (openTalent?.linkedin_profile || "")) {
                      changes.linkedin_profile = values.linkedin_profile;
                    }
                    if (values.website_or_portfolio !== (openTalent?.website_or_portfolio || "")) {
                      changes.website_or_portfolio = values.website_or_portfolio;
                    }
                    if (values.hourly_rate !== (openTalent?.hourly_rate || "")) {
                      changes.hourly_rate = values.hourly_rate;
                    }
                    if (!Object.keys(changes).length) {
                      setIsSectionSaving(false);
                      setIsProfessionalEditOpen(false);
                      return;
                    }
                    const formData = new FormData();
                    Object.entries(changes).forEach(([key, value]) => {
                      formData.append(key, value);
                    });
                    dispatch(
                      freelancerUpdateProfileStep({
                        payload: formData,
                        callback: (response) => {
                          setIsSectionSaving(false);
                          if (response?.success) {
                            dispatch(fetchfreelancerProfile());
                          }
                          setIsProfessionalEditOpen(false);
                        },
                      })
                    );
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    setFieldError,
                    setFieldTouched,
                  }) => (
                    <form
                      onSubmit={handleSubmit}
                      className="ct_modal_scroll ct_custom_scroll px-4 pb-3 "
                    >
                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              Primary Role / Profession
                            </label>
                            <div className="d-flex flex-column gap-2 position-relative">
                              <select
                                className="form-control ct_input"
                                name="primary_role"
                                value={values.primary_role}
                                onChange={(event) => {
                                  const nextValue = event.target.value;
                                  setFieldValue("primary_role", nextValue);
                                  if (nextValue !== "Other") {
                                    setFieldValue("other_primary_role", "");
                                  }
                                }}
                                onBlur={handleBlur}
                              >
                                <option value="">Select role</option>
                                {roleOptions.map((role) => (
                                  <option key={role} value={role}>
                                    {role}
                                  </option>
                                ))}
                              </select>
                              <i className="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                            </div>
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="primary_role"
                            />
                          </div>
                        </div>
                        {values.primary_role === "Other" && (
                          <div className="col-md-12">
                            <div className="form-group mb-4">
                              <label className="ct_fs_14 ct_darkblue_text mb-2">
                                Enter Primary Role
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                name="other_primary_role"
                                value={values.other_primary_role}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="other_primary_role"
                              />
                            </div>
                          </div>
                        )}
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              Availability
                            </label>
                            <div className="d-flex flex-column gap-2">
                              {availabilityOptions.map((option) => (
                                <label key={option.value} className="ct_checkbox_label d-flex gap-2">
                                  <input
                                    type="checkbox"
                                    checked={(values.availability || []).includes(option.value)}
                                    onChange={() =>
                                      toggleAvailability(
                                        values.availability,
                                        option.value,
                                        setFieldValue,
                                        setFieldError,
                                        setFieldTouched
                                      )
                                    }
                                  />
                                  <span className="ct_fs_14 ct_darkblue_text">
                                    {option.label}
                                  </span>
                                </label>
                              ))}
                            </div>
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="availability"
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              LinkedIn Profile
                            </label>
                            <input
                              type="text"
                              className="form-control ct_input"
                              placeholder="LinkedIn Profile"
                              name="linkedin_profile"
                              value={values.linkedin_profile}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="linkedin_profile"
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              Portfolio (Optional)
                            </label>
                            <input
                              type="text"
                              className="form-control ct_input"
                              placeholder="Portfolio"
                              name="website_or_portfolio"
                              value={values.website_or_portfolio}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="website_or_portfolio"
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="ct_fs_14 ct_darkblue_text mb-2">
                              Hourly Rate (Optional)
                            </label>
                            <input
                              type="number"
                              className="form-control ct_input"
                              placeholder="Hourly Rate"
                              name="hourly_rate"
                              value={values.hourly_rate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3 justify-content-end pt-4">
                        <button
                          className="ct_sky_blue_btn ct_fs_14 ct_yellow_btn ct_outline_grey  px-5 py-2"
                          type="button"
                          onClick={() => setIsProfessionalEditOpen(false)}
                          disabled={isSectionSaving}
                        >
                          Cancel
                        </button>
                        <button
                          className=" ct_yellow_btn px-5 ct_fs_14 py-2"
                          type="submit"
                          disabled={isSectionSaving}
                        >
                          {isSectionSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}

      {isPreferredTypeEditOpen && (
        <div className="modal modal-lg ct_congratulation_modal_fade d-block">
          <div className="modal-dialog modal-dialog-centered ct_modal-dialog">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h4 className="ct_fs_16 ct_darkblue_text mb-0">
                  Preferred Project Type
                </h4>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => setIsPreferredTypeEditOpen(false)}
                ></button>
              </div>
              <div className="modal-body px-0 ct_mt_20 pt-0">
                <Formik
                  initialValues={{
                    preferred_project_type: preferredProjectTypes,
                  }}
                  onSubmit={(values) => {
                    setIsSectionSaving(true);
                    const next = normalizeList(values.preferred_project_type);
                    const prev = normalizeList(preferredProjectTypes);
                    if (toCommaString(next) === toCommaString(prev)) {
                      setIsSectionSaving(false);
                      setIsPreferredTypeEditOpen(false);
                      return;
                    }
                    const formData = new FormData();
                    formData.append("preferred_project_type", toCommaString(next));
                    dispatch(
                      freelancerUpdateProfileStep({
                        payload: formData,
                        callback: (response) => {
                          setIsSectionSaving(false);
                          if (response?.success) {
                            dispatch(fetchfreelancerProfile());
                          }
                          setIsPreferredTypeEditOpen(false);
                        },
                      })
                    );
                  }}
                >
                  {({ values, handleSubmit, setFieldValue }) => (
                    <form
                      onSubmit={handleSubmit}
                      className="ct_modal_scroll ct_custom_scroll px-4 pb-3 "
                    >
                      <div className="mb-4 d-flex flex-wrap gap-3">
                        {projectTypeOptions.map((option) => (
                          <label key={option} className="d-flex align-items-center gap-2">
                            <input
                              type="checkbox"
                              checked={values.preferred_project_type.includes(option)}
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setFieldValue("preferred_project_type", [
                                    ...values.preferred_project_type,
                                    option,
                                  ]);
                                } else {
                                  setFieldValue(
                                    "preferred_project_type",
                                    values.preferred_project_type.filter((item) => item !== option)
                                  );
                                }
                              }}
                            />
                            <span className="ct_fs_14 ct_darkblue_text">{option}</span>
                          </label>
                        ))}
                      </div>
                      <div className="d-flex align-items-center gap-3 justify-content-end pt-4">
                        <button
                          className="ct_sky_blue_btn ct_fs_14 ct_yellow_btn ct_outline_grey  px-5 py-2"
                          type="button"
                          onClick={() => setIsPreferredTypeEditOpen(false)}
                          disabled={isSectionSaving}
                        >
                          Cancel
                        </button>
                        <button
                          className=" ct_yellow_btn px-5 ct_fs_14 py-2"
                          type="submit"
                          disabled={isSectionSaving}
                        >
                          {isSectionSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}

      {isToolsEditOpen && (
        <div className="modal modal-lg ct_congratulation_modal_fade d-block">
          <div className="modal-dialog modal-dialog-centered ct_modal-dialog">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h4 className="ct_fs_16 ct_darkblue_text mb-0">
                  Tools & Software Used
                </h4>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => setIsToolsEditOpen(false)}
                ></button>
              </div>
              <div className="modal-body px-0 ct_mt_20 pt-0">
                <Formik
                  initialValues={{ tools: toolsUsed }}
                  validationSchema={toolsModalSchema}
                  onSubmit={(values) => {
                    setIsSectionSaving(true);
                    const next = normalizeList(values.tools);
                    const prev = normalizeList(toolsUsed);
                    if (toCommaString(next) === toCommaString(prev)) {
                      setIsSectionSaving(false);
                      setIsToolsEditOpen(false);
                      return;
                    }
                    const formData = new FormData();
                    formData.append("tools_software_used", toCommaString(next));
                    dispatch(
                      freelancerUpdateProfileStep({
                        payload: formData,
                        callback: (response) => {
                          setIsSectionSaving(false);
                          if (response?.success) {
                            dispatch(fetchfreelancerProfile());
                          }
                          setIsToolsEditOpen(false);
                        },
                      })
                    );
                  }}
                >
                  {({ values, errors, touched, handleSubmit, setFieldValue }) => (
                    <form
                      onSubmit={handleSubmit}
                      className="ct_modal_scroll ct_custom_scroll px-4 pb-3 "
                    >
                      <TagInput
                        name="tools"
                        value={values.tools}
                        onChange={(name, val) => setFieldValue(name, val)}
                        placeholder="Add tools"
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="tools"
                      />
                      <div className="d-flex align-items-center gap-3 justify-content-end pt-4">
                        <button
                          className="ct_sky_blue_btn ct_fs_14 ct_yellow_btn ct_outline_grey  px-5 py-2"
                          type="button"
                          onClick={() => setIsToolsEditOpen(false)}
                          disabled={isSectionSaving}
                        >
                          Cancel
                        </button>
                        <button
                          className=" ct_yellow_btn px-5 ct_fs_14 py-2"
                          type="submit"
                          disabled={isSectionSaving}
                        >
                          {isSectionSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSkillsEditOpen && (
        <div className="modal modal-lg ct_congratulation_modal_fade d-block">
          <div className="modal-dialog modal-dialog-centered ct_modal-dialog">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h4 className="ct_fs_16 ct_darkblue_text mb-0">Skills</h4>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => setIsSkillsEditOpen(false)}
                ></button>
              </div>
              <div className="modal-body px-0 ct_mt_20 pt-0">
                <Formik
                  initialValues={{ skills: keySkills }}
                  validationSchema={skillsModalSchema}
                  onSubmit={(values) => {
                    setIsSectionSaving(true);
                    const next = normalizeList(values.skills);
                    const prev = normalizeList(keySkills);
                    if (toCommaString(next) === toCommaString(prev)) {
                      setIsSectionSaving(false);
                      setIsSkillsEditOpen(false);
                      return;
                    }
                    const formData = new FormData();
                    formData.append("skills", toCommaString(next));
                    dispatch(
                      freelancerUpdateProfileStep({
                        payload: formData,
                        callback: (response) => {
                          setIsSectionSaving(false);
                          if (response?.success) {
                            dispatch(fetchfreelancerProfile());
                          }
                          setIsSkillsEditOpen(false);
                        },
                      })
                    );
                  }}
                >
                  {({ values, errors, touched, handleSubmit, setFieldValue }) => (
                    <form
                      onSubmit={handleSubmit}
                      className="ct_modal_scroll ct_custom_scroll px-4 pb-3 "
                    >
                      <TagInput
                        name="skills"
                        value={values.skills}
                        onChange={(name, val) => setFieldValue(name, val)}
                        placeholder="Add skills"
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="skills"
                      />
                      <div className="d-flex align-items-center gap-3 justify-content-end pt-4">
                        <button
                          className="ct_sky_blue_btn ct_fs_14 ct_yellow_btn ct_outline_grey  px-5 py-2"
                          type="button"
                          onClick={() => setIsSkillsEditOpen(false)}
                          disabled={isSectionSaving}
                        >
                          Cancel
                        </button>
                        <button
                          className=" ct_yellow_btn px-5 ct_fs_14 py-2"
                          type="submit"
                          disabled={isSectionSaving}
                        >
                          {isSectionSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}

      {isWorkSamplesEditOpen && (
        <div className="modal modal-lg ct_congratulation_modal_fade d-block">
          <div className="modal-dialog modal-dialog-centered ct_modal-dialog">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h4 className="ct_fs_16 ct_darkblue_text mb-0">Work Samples</h4>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => setIsWorkSamplesEditOpen(false)}
                ></button>
              </div>
              <div className="modal-body px-0 ct_mt_20 pt-0">
                <Formik
                  initialValues={{
                    portfolio_links: normalizeList(openTalent?.sample_portfolio_link || [""]),
                    work_samples: workSamplesCombined,
                  }}
                  validationSchema={workSamplesModalSchema}
                  onSubmit={(values) => {
                    setIsSectionSaving(true);
                    const nextLinks = normalizeList(values.portfolio_links);
                    const prevLinks = normalizeList(openTalent?.sample_portfolio_link || []);
                    const changes = {};
                    if (toCommaString(nextLinks) !== toCommaString(prevLinks)) {
                      changes.sample_portfolio_link = toCommaString(nextLinks);
                    }
                    const formData = new FormData();
                    Object.entries(changes).forEach(([key, value]) => {
                      formData.append(key, value);
                    });
                    (values.work_samples || []).forEach((file) => {
                      if (file instanceof File) {
                        formData.append("projects", file);
                      }
                    });
                    dispatch(
                      freelancerUpdateProfileStep({
                        payload: formData,
                        callback: (response) => {
                          setIsSectionSaving(false);
                          if (response?.success) {
                            dispatch(fetchfreelancerProfile());
                          }
                          setIsWorkSamplesEditOpen(false);
                        },
                      })
                    );
                  }}
                >
                  {({ values, errors, touched, handleSubmit, setFieldValue }) => (
                    <form
                      onSubmit={handleSubmit}
                      className="ct_modal_scroll ct_custom_scroll px-4 pb-3 "
                    >
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Work Sample Links
                        </label>
                        <div className="d-flex flex-column gap-2">
                          {values.portfolio_links.map((link, index) => (
                            <div key={index} className="d-flex flex-column gap-2">
                              <div className="d-flex gap-2 align-items-center">
                                <input
                                  type="text"
                                  className="form-control ct_input"
                                  value={link}
                                  onChange={(event) =>
                                    setFieldValue(
                                      `portfolio_links.${index}`,
                                      event.target.value
                                    )
                                  }
                                />
                                {values.portfolio_links.length > 1 && (
                                  <button
                                    type="button"
                                    className="ct_outline_btn ct_h_32 ct_btn_inline"
                                    onClick={() =>
                                      setFieldValue(
                                        "portfolio_links",
                                        values.portfolio_links.filter((_, i) => i !== index)
                                      )
                                    }
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName={`portfolio_links.${index}`}
                              />
                            </div>
                          ))}
                          <button
                            type="button"
                            className="ct_yellow_btn ct_outline_grey ct_h_44 align-self-start"
                            onClick={() =>
                              setFieldValue("portfolio_links", [
                                ...values.portfolio_links,
                                "",
                              ])
                            }
                          >
                            Add Link
                          </button>
                        </div>
                        {typeof errors?.portfolio_links === "string" && (
                          <span style={{ color: "red" }}>{errors.portfolio_links}</span>
                        )}
                      </div>
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Upload Work Samples (ZIP)
                        </label>
                        <PdfOrImageUploader
                          maxFiles={10}
                          multiple={true}
                          acceptType=".zip,application/zip"
                          placeholder="Upload Work Samples (ZIP only)"
                          replaceOnSelect={false}
                          existingFiles={
                            Array.isArray(values.work_samples)
                              ? values.work_samples.map((file, idx) => {
                                const isFile = file instanceof File;
                                const rawUrl =
                                  typeof file === "string"
                                    ? file
                                    : file?.project_url || file?.url || file?.preview || "";
                                const name =
                                  file?.name ||
                                  (typeof rawUrl === "string" && rawUrl
                                    ? rawUrl.split("/").pop()
                                    : `sample-${idx + 1}.zip`);
                                return {
                                  id: file?.id || file?.project_id || file?.open_talent_project_id || idx,
                                  file: isFile ? file : rawUrl || file,
                                  name,
                                  type: "zip",
                                  preview: rawUrl || null,
                                  isExisting: !isFile,
                                };
                              })
                              : []
                          }
                          onChange={(newFiles) => setFieldValue("work_samples", newFiles)}
                        />
                      </div>
                      <div className="d-flex align-items-center gap-3 justify-content-end pt-4">
                        <button
                          className="ct_sky_blue_btn ct_fs_14 ct_yellow_btn ct_outline_grey  px-5 py-2"
                          type="button"
                          onClick={() => setIsWorkSamplesEditOpen(false)}
                          disabled={isSectionSaving}
                        >
                          Cancel
                        </button>
                        <button
                          className=" ct_yellow_btn px-5 ct_fs_14 py-2"
                          type="submit"
                          disabled={isSectionSaving}
                        >
                          {isSectionSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}

      {isDocumentsEditOpen && (
        <div className="modal modal-lg ct_congratulation_modal_fade d-block">
          <div className="modal-dialog modal-dialog-centered ct_modal-dialog">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h4 className="ct_fs_16 ct_darkblue_text mb-0">Documents</h4>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => setIsDocumentsEditOpen(false)}
                ></button>
              </div>
              <div className="modal-body px-0 ct_mt_20 pt-0">
                <Formik
                  initialValues={{
                    cv: cvFiles || [],
                    certificates: certificateFiles || [],
                  }}
                  validationSchema={documentsModalSchema}
                  onSubmit={(values) => {
                    setIsSectionSaving(true);
                    const formData = new FormData();
                    (values.cv || []).forEach((file) => {
                      if (file instanceof File) {
                        formData.append("cv", file);
                      }
                    });
                    (values.certificates || []).forEach((file) => {
                      if (file instanceof File) {
                        formData.append("certificates", file);
                      }
                    });
                    dispatch(
                      freelancerUpdateProfileStep({
                        payload: formData,
                        callback: (response) => {
                          setIsSectionSaving(false);
                          if (response?.success) {
                            dispatch(fetchfreelancerProfile());
                          }
                          setIsDocumentsEditOpen(false);
                        },
                      })
                    );
                  }}
                >
                  {({ values, errors, touched, handleSubmit, setFieldValue }) => (
                    <form
                      onSubmit={handleSubmit}
                      className="ct_modal_scroll ct_custom_scroll px-4 pb-3 "
                    >
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Upload CV (PDF only)
                        </label>
                        <PdfOrImageUploader
                          maxFiles={1}
                          multiple={false}
                          acceptType="application/pdf,.pdf"
                          placeholder="Upload CV (PDF only)"
                          replaceOnSelect={true}
                          existingFiles={
                            Array.isArray(values.cv)
                              ? values.cv.map((file, idx) => ({
                                id: file?.id || idx,
                                file: file,
                                name:
                                  file?.name ||
                                  (typeof file === "string"
                                    ? file.split("/").pop()
                                    : `cv-${idx + 1}.pdf`),
                                type: "pdf",
                                preview:
                                  typeof file === "string"
                                    ? file
                                    : file?.preview || file?.url || "",
                                isExisting: !(file instanceof File),
                              }))
                              : []
                          }
                          onChange={(newFiles) => setFieldValue("cv", newFiles)}
                        />
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="cv"
                        />
                      </div>
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Upload Certificates (PDF or Image)
                        </label>
                        <PdfOrImageUploader
                          maxFiles={5}
                          multiple={true}
                          acceptType="application/pdf,image/*"
                          placeholder="Upload Certificates (PDF or Image)"
                          existingFiles={
                            Array.isArray(values.certificates)
                              ? values.certificates.map((file, idx) => ({
                                id: file?.id || file?.certificate_id || idx,
                                file: file,
                                name:
                                  file?.name ||
                                  (typeof file === "string"
                                    ? file.split("/").pop()
                                    : `certificate-${idx + 1}`),
                                type:
                                  typeof file === "string"
                                    ? file.toLowerCase().includes(".pdf")
                                      ? "pdf"
                                      : "image"
                                    : file?.type
                                      ? file.type
                                      : (file?.url || file?.preview || file?.file || "")
                                        .toLowerCase()
                                        .includes(".pdf")
                                        ? "pdf"
                                        : "image",
                                preview:
                                  typeof file === "string"
                                    ? file
                                    : file?.preview || file?.url || file?.file || "",
                                isExisting: !(file instanceof File),
                              }))
                              : []
                          }
                          onChange={(newFiles) =>
                            setFieldValue("certificates", newFiles)
                          }
                        />
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="certificates"
                        />
                      </div>
                      <div className="d-flex align-items-center gap-3 justify-content-end pt-4">
                        <button
                          className="ct_sky_blue_btn ct_fs_14 ct_yellow_btn ct_outline_grey  px-5 py-2"
                          type="button"
                          onClick={() => setIsDocumentsEditOpen(false)}
                          disabled={isSectionSaving}
                        >
                          Cancel
                        </button>
                        <button
                          className=" ct_yellow_btn px-5 ct_fs_14 py-2"
                          type="submit"
                          disabled={isSectionSaving}
                        >
                          {isSectionSaving ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="modal modal-lg ct_congratulation_modal_fade"
        id="ct_add_portfolio_modal"
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered ct_modal-dialog">
          <div className="modal-content">
            <div className="modal-header border-0">
              <h4 className="ct_fs_16 ct_darkblue_text mb-0">
                Add New Portfolio
              </h4>
              <button
                type="button"
                className="btn-close ct_circle_close"
                data-bs-dismiss="modal"
              ></button>
            </div>
            <div className="modal-body px-0 ct_mt_20 pt-0">
              <form className="ct_modal_scroll ct_custom_scroll px-4 pb-3 ">
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group mb-4">
                      <label className="ct_fs_14 ct_darkblue_text mb-2">
                        Project Title
                      </label>
                      <input
                        type="text"
                        className="form-control ct_fs_12 ct_input"
                        placeholder=" Project title"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="ct_fs_14 ct_darkblue_text mb-2">
                        Role in Project
                      </label>
                      <input
                        type="text"
                        className="form-control ct_fs_12 ct_input"
                        placeholder=" Role in project"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="ct_fs_14 ct_darkblue_text mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="form-control ct_fs_12 ct_input"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="ct_fs_14 ct_darkblue_text mb-2">
                        Team Member
                      </label>
                      <input
                        type="text"
                        className="form-control ct_fs_12 ct_input"
                        placeholder="Enter Team Member"
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="ct_fs_14 ct_darkblue_text mb-2">
                        Budget
                      </label>
                      <input
                        type="text"
                        className="form-control ct_fs_12 ct_input"
                        placeholder="Enter Budget"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="ct_fs_14 ct_darkblue_text mb-2">
                        Project Duration
                      </label>
                      <div className="position-relative">
                        <input
                          type="number"
                          className="form-control ct_input"
                          style={{ paddingLeft: "80px" }}
                        />
                        <div className="d-flex flex-column gap-2 position-relative">
                          <select
                            className="ct_input ct_fs_12 ct_day_select"
                            style={{ height: "auto" }}
                          >
                            <option value="month">Month</option>
                            <option value="day">Day</option>
                          </select>
                          <i class="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <TagInput
                    label="Skills"
                    placeholder="Type to add skills..."
                    name="project_skills"
                    value={projectSkills}
                    onChange={(name, value) => setProjectSkills(value)}
                  />

                  <div className="col-md-12">
                    <div className="form-group mb-4">
                      <label className="ct_fs_14 ct_darkblue_text mb-2">
                        Project Description
                      </label>
                      <textarea
                        className="form-control ct_input h-auto ct_fs_12"
                        rows={4}
                        placeholder="Tell a brief story: what was the goal, your contribution and the impact (max 500 characters)"
                      ></textarea>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form-group mb-4">
                      <label className="ct_fs_14 ct_darkblue_text mb-2">
                        Upload Portfolio Image (2 max)
                      </label>
                      <div
                        class="ct_upload_product_main ct_upload_product_main_180 "
                        style={{ height: "118px" }}
                      >
                        <svg
                          width="33"
                          height="33"
                          viewBox="0 0 33 33"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M5.87174 22.0167C4.26376 20.9403 3.20508 19.1073 3.20508 17.027C3.20508 13.9023 5.59376 11.3354 8.64473 11.0528C9.26883 7.25653 12.5654 4.36035 16.5384 4.36035C20.5114 4.36035 23.808 7.25653 24.4321 11.0528C27.4831 11.3354 29.8717 13.9023 29.8717 17.027C29.8717 19.1073 28.8131 20.9403 27.2051 22.0167M11.2051 21.6937L16.5384 16.3604M16.5384 16.3604L21.8717 21.6937M16.5384 16.3604V28.3604"
                            stroke="#9CA3AF"
                            stroke-width="2.66667"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          ></path>
                        </svg>
                        <p class="text-center ct_fs_12 ct_text_op_07 mb-0">
                          Upload your Portfolio here
                        </p>
                        <label for="ct_upload_product">
                          <input
                            type="file"
                            name=""
                            id="ct_upload_product"
                            class="d-none"
                          />
                        </label>
                      </div>
                      <div class="ct_single_uploaded_img mt-3 d-none">
                        <img
                          src="assets/img/Why_Be_Part_of_NUVIO_left_img.png"
                          alt=""
                        />
                        <i class="fa-solid fa-xmark"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 justify-content-end pt-4">
                  <button
                    className="ct_sky_blue_btn ct_fs_14 ct_yellow_btn ct_outline_grey  px-5 py-2"
                    style={{ borderColorL: "#53586252" }}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className=" ct_yellow_btn px-5 ct_fs_14 py-2"
                    type="button"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </FreelancerLayout >
  );
};

export default Profile;
